/**
 * Image MIME types a browser can actually decode and paint into an <img>.
 * The `image` asset category is far broader than this — TIFF, PSD, PSB, DXF,
 * HEIC and AI all classify as images but no mainstream browser renders them —
 * so pointing an <img> at one produces the browser's broken-image glyph rather
 * than a preview. Every preview surface gates on this list and falls through to
 * the category placeholder instead.
 */
export const BROWSER_RENDERABLE_IMAGE_MIME_TYPES: ReadonlyArray<string> = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/apng',
  'image/svg+xml',
  'image/x-icon',
  'image/vnd.microsoft.icon',
];

/**
 * Subset of the renderable types that may be cropped. SVG is excluded because
 * rasterising it through a canvas is lossy and taints the canvas in some
 * browsers, and icon formats carry multiple resolutions in one file.
 */
export const CROPPABLE_IMAGE_MIME_TYPES: ReadonlyArray<string> = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/apng',
];

/**
 * Image MIME types the browser cannot decode but sharp can, so the server is able to build a
 * viewable thumbnail from the original on demand. PSD is deliberately absent: the installed
 * sharp build has no ImageMagick buffer input, so it cannot read one.
 */
export const SERVER_THUMBNAILABLE_IMAGE_MIME_TYPES: ReadonlyArray<string> = [
  'image/tiff',
  'image/x-tiff',
  'image/heic',
  'image/heif',
];

/** Strips any charset/parameter suffix and casing so lookups match a bare MIME type. */
function normaliseMimeType(fileType: string | null | undefined): string {
  if (!fileType) return '';
  return (fileType.toLowerCase().split(';')[0] ?? '').trim();
}

/** True when the MIME type is an image at all, as opposed to video, audio or a document. */
export function isImageMimeType(fileType: string | null | undefined): boolean {
  return normaliseMimeType(fileType).startsWith('image/');
}

/** True when the browser can paint this MIME type into an <img> element. */
export function isBrowserRenderableImage(fileType: string | null | undefined): boolean {
  return BROWSER_RENDERABLE_IMAGE_MIME_TYPES.includes(normaliseMimeType(fileType));
}

/** True when the server can generate a viewable thumbnail for a type the browser cannot render. */
export function isServerThumbnailableImage(fileType: string | null | undefined): boolean {
  return SERVER_THUMBNAILABLE_IMAGE_MIME_TYPES.includes(normaliseMimeType(fileType));
}

/**
 * True when the asset has any viewable form at all — either the browser decodes it directly or
 * the server can generate a thumbnail. False means no preview will ever arrive, so callers show
 * their placeholder immediately rather than waiting on a URL that never resolves.
 */
export function isPreviewableImage(fileType: string | null | undefined): boolean {
  return isBrowserRenderableImage(fileType) || isServerThumbnailableImage(fileType);
}

/** True when the MIME type can be loaded into the crop tool's canvas. */
export function isCroppableImage(fileType: string | null | undefined): boolean {
  return CROPPABLE_IMAGE_MIME_TYPES.includes(normaliseMimeType(fileType));
}
